import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, SettingDTO, SettingFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { SettingMaintenance } from './SettingMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';

export const Setting: React.FC = () => {

    const [settingList, setSettingList] = useState<SettingDTO[]>([]);
    const [settingFilterDTO, setSettingFilterDTO] = useState<SettingFilterDTO>();
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setSettingFilterDTO({
            ...settingFilterDTO,
            name: '',
            description: '',
            value: '',
            companyId: userInfo.user.companyId
        } as SettingFilterDTO);
    }, []);
    
    useEffect(() => {
        handleCompanyGetAll();
        handleSettingGetAllViewAsync();

        handleGetPermission(ListId.parmsphere.settingId, setAccessRolePermissionDTO);
    }, [settingFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleSettingGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);
    
    const handleSettingGetAllViewAsync = useCallback(async () => {
        if (settingFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return; 
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        }
        GenericService.GetAllViewAsync1('parmsphere.setting', settingFilterDTO, setActionExecuted, setLoading, setSettingList, showMessage, true);
    }, [settingFilterDTO, accessRolePermissionDTO]);

    const handleCompanyGetAll = useCallback(async () => {
        const companyFIlterDTO: CompanyFilterDTO = {
            name: '',
            businessRelationshipTypeId: '',
            statusId: '',
            orderBy: 'name ASC'
        };
        GenericService.GetAllAsync1('corpsphere.company', companyFIlterDTO, setLoading, setCompanyList, showMessage);
    }, []);

    const handleClearFilterClick = () => {
        setSettingFilterDTO({ ...settingFilterDTO, name: '', description: '', value: '', companyId: userInfo.user.companyId } as SettingFilterDTO);
    };

    function handleSearchClick() {
        handleSettingGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage('There is no permission for new record.', 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage('There is no permission for view details.', 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage('There is no permission for delete record.', 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Setting', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: settingList.length },
        { label: 'Name', value: settingFilterDTO?.name },
        { label: 'Company', value: companyList.find(x => x.id === settingFilterDTO?.companyId)?.name },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'value', label: 'Value', size: 5, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'company', label: 'Company', size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: false },
        { field: 'isGlobal', label: 'Is Global', size: 1, type: 'boolean', visible: true, isChip: true, hasFieldLine: false, hasToolTip: false },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='SETTING'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setSettingFilterDTO}
                                value={settingFilterDTO?.name}
                                obj={settingFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='companyId'
                                label='Company'
                                setValue={setSettingFilterDTO}
                                value={settingFilterDTO?.companyId}
                                obj={settingFilterDTO}
                                valueList={companyList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '79vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={settingList}
                        actions={cardActions}
                        hasChildren={false}
                        setRowExpanded={() => void undefined}
                        cardSize={12}
                        childrenTitle={'SETTING'}
                    />
                </Box>
                {newRecord && (
                    <SettingMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <SettingMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'parmsphere.setting'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};